import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromTermsConditions from './store/reducers/terms-cond.reducers';
import * as fromDashboard from '@app/features/dashboard/store/reducers/dashboard.reducer';
import * as fromAuth from '@app/auth/store/reducers/auth.reducer';
import * as fromDocuments from '@app/features/profile/store/reducers/profile.reducer';
import * as fromUberall from './store/reducers/uberall.reducers';
import * as fromGooglePost from '@app/features/google-post/store/reducers/google-post.reducer';

export const reducerName = 'main';

export interface MainState {
  termsConditions: fromTermsConditions.TermsState;
  dashboard: fromDashboard.DashBoardState;
  auth: fromAuth.AuthState;
  profile: fromDocuments.ProfileState;
  uberall: fromUberall.UberallState;
  googlePost: fromGooglePost.GooglePostState;
}

export const reducers: ActionReducerMap<MainState> = {
  termsConditions: fromTermsConditions.TermsReducer,
  dashboard: fromDashboard.reducer,
  auth: fromAuth.AuthReducer,
  profile: fromDocuments.profileReducer,
  uberall: fromUberall.uberallReducer,
  googlePost: fromGooglePost.googlePostReducer,
};

const getModuleState = createFeatureSelector<MainState>(reducerName);

// Loading
export const getLoading = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.loading);

// Terms & Conditions
export const getTermsConditionsState = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions);

export const getTermsConditions = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.termsConditions);

export const getIsDishAccepted = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.isDishAccepted);

export const getEstablisments = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.establishments);

export const getDefaultEstablismentId = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.establishmentDefaultId);

export const getDefaultEstablisment = createSelector(getModuleState, (stateMain: MainState) =>
  stateMain ? stateMain.termsConditions.establishmentDefault : null
);

export const isSettingsPageOpen = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.isSettingsPage);

export const getCategoriesCountries = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.countriesAndCategories);

export const getBusinessCompletenes = createSelector(getModuleState, (stateMain: MainState) => stateMain.dashboard.completeness);

export const getCurrentAdmins = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.currentAdmins);

export const sendInviteStatus = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.inviteAdminSuccess);

export const getMainError = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.hasBootstrapError);

export const getMigrationError = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.hasMigrationError);

export const getMigrationErrorKeys = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.migrationErrorKeys);

export const isDishUserWithNoEstablishmentScan = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.dishUserWithNoEstablishmentScan;
});

export const getBusinessUnitActions = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.businessUnitActions;
});

export const getUserActions = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.userActions;
});

export const getUpgradeActions = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.upgradeActions;
});

export const getCountryCode = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.establishmentDefault?.countryCode);

// Dashboard
export const getDashboardState = createSelector(getModuleState, (stateMain: MainState) => stateMain.dashboard.business);

export const getSelectedBusiness = createSelector(getModuleState, (stateMain: MainState) => stateMain.dashboard.selectedBusinessUnit);

export const getProfileValidationErrors = createSelector(getModuleState, (stateMain: MainState) => stateMain.dashboard.profileValidationErrors);

// Auth - OnBoarding
export const getAuth = createSelector(getModuleState, (stateMain: MainState) => stateMain.auth.hasManageError);

export const language = createSelector(getModuleState, (stateMain: MainState) => stateMain.auth.language);

export const restaurantAssistent = createSelector(getModuleState, (stateMain: MainState) => stateMain.auth.restaurantAssistent);

export const loading = createSelector(getModuleState, (stateMain: MainState) => stateMain.auth.loading);

export const errorMessage = createSelector(getModuleState, (stateMain: MainState) => stateMain.auth.errorMessage);

export const getNavBarState = createSelector(getModuleState, (stateMain: MainState) => stateMain.auth.showNavMenu);

export const getEstablishmentCategory = createSelector(
  getModuleState,
  (stateMain: MainState) => stateMain.termsConditions.establishmentDefault.category
);

export const getEstablishmentCategoryProfile = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.establishmentDefault.category;
});

export const getPurchasedProduct = createSelector(getModuleState, (stateMain: MainState) => stateMain.termsConditions.termsConditions.products);

// Account Settings (Admin)
export const getProfileBusinessList = createSelector(getModuleState, (state: MainState) => state.profile.business);

export const getOpenClaimingStatus = createSelector(getModuleState, (state: MainState) => state.dashboard.openClaimingDialog);

export const getShowSpecialGMBInfoStatus = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.showSpecialGMBInfo;
});

export const getIsBusinessFromHydra = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.auth.isBusinessFromHydra;
});

export const getIsAfterOnboardingWizardStatus = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.auth.isAfterOnboardingWizard;
});

export const getClaimingStage = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.dashboard.claimingStage;
});

export const getLookupSuccessFlag = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.dashboard.lookupSuccess;
});

export const getFaqs = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.faqs;
});

export const getActivateGMBChannelError = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.activateGMBFailed;
});

export const getResetGMBChannelError = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.resetGMBfailed;
});

export const getGhostLoginGMBActionLoadingFlag = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.termsConditions.ghostLoginGMBActionsLoading;
});

export const didMacUserLookupFailed = createSelector(getModuleState, (stateMain: MainState) => {
  return stateMain.dashboard.didMacUserLookupFailed;
});

export const getUberallAccessToken = createSelector(getModuleState, (stateMain: MainState) => {
  const { uberall } = stateMain;
  return uberall.accessToken;
});

export const getUberallLocationId = createSelector(getModuleState, (stateMain: MainState) => {
  const { uberall } = stateMain;
  return uberall.locationId;
});

export const updateBusinessDataSuccess = createSelector(getModuleState, (stateMain: MainState) => {
  const { updateBusinessDataSuccess } = stateMain.termsConditions;
  return updateBusinessDataSuccess;
});

export const getAnalyticsdata = createSelector(getModuleState, (state: MainState) => state.uberall.analyticsData);

export const getAnalyticsFetching = createSelector(getModuleState, (state: MainState) => state.uberall.fetchingAnalytics);

export const getAnalyticsFetchingFailed = createSelector(getModuleState, (state: MainState) => state.uberall.fetchingAnalyticsFailed);

export const isGoogleListingConnected = createSelector(getModuleState, (stateMain: MainState) => {
  const [businessAction] = stateMain.termsConditions.businessUnitActions || [];
  const { actionType, payload } = businessAction;

  if (actionType.includes('SHOW_BASIC') && payload) {
    const [channel] = payload.businessUnit.channels;
    return !!channel.searchUrl;
  }

  return false;
});

// Google Post
export const getImages = createSelector(getModuleState, (state: MainState) => {
  return state?.googlePost.images;
});

export const getPublishedPosts = createSelector(getModuleState, (state: MainState) => {
  return state?.googlePost.publishedPosts;
});
